import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Spin, Table, Tag, Tooltip, Typography} from "antd";
import {DeleteTwoTone, EditTwoTone} from "@ant-design/icons";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import {dateFormat} from "../../lib/time";
import {ucFirst} from "../../helpers/misc";
import EngageCurrency from "./EngageCurrency";
import ChargeCreateDrawer from "./ChargeCreateDrawer";
import ChargeEditDrawer from "./ChargeEditDrawer";
import {deleteCharge, getCharges} from "../../helpers/billing";
import confirmAction from "../../helpers/confirmAction";
import {messageType, showMessages} from "../../helpers/message";
import {chargeDeleteSuccessMessage, chargeUpdateSuccessMessage} from "../../helpers/text";
import {getAccountMembershipNumberPerson, getActiveAccount, getActiveProfile} from "../../lib/activeMember";

import {black, successGreen} from "../../assets/colors";
import "./ChargeHistory.less";

const columns = [
    {
        title: <Typography.Text strong>Charge No.</Typography.Text>,
        dataIndex: 'charge_number',
        render: charge_number =>
            <span className="charge-num-line-item long-text-wrap">{charge_number}</span>
    },
    {
        title: <Typography.Text strong>Date</Typography.Text>,
        dataIndex: 'charged_at',
        render: charged_at =>
            <span className="date-line-item long-text-wrap">{dayjs(charged_at).tz().format(dateFormat)}</span>,
    },
    {
        title: <Typography.Text strong>Charge Category</Typography.Text>,
        dataIndex: 'type',
        render: type =>
            <span className="category-line-item long-text-wrap">{ucFirst(type)}</span>
    },
    {
        title: <Typography.Text strong>Charge Name</Typography.Text>,
        dataIndex: 'name',
        render: name =>
            <span className="name-line-item long-text-wrap">{name ? ucFirst(name) : 'N/A'}</span>
    },
    {
        title: <Typography.Text strong>Status</Typography.Text>,
        dataIndex: 'status',
        key: 'status',
        render: status => {
            let colour;
            switch (status) {
                case 'pending':
                    colour = 'gold'
                    break;
                case 'reconciled':
                    colour = 'geekblue'
                    break;
                case 'invoiced':
                    colour = 'green'
                    break;
                case 'Reconciled / Invoiced':
                    colour = 'purple'
                    break;
                case null:
                    colour = 'default'
                    break;
                default:
                    return null;
            }
            return (
            <>
                <span className="status-line-item">Status: </span>
                <Tag color={colour} key={status}>{status ? ucFirst(status) : 'N/A'}</Tag>
            </>
            )
        },
    },
    {
        title: <Typography.Text strong>Amount</Typography.Text>,
        dataIndex: 'amount',
        render: (amount, row) => {
            let totalTax = row.taxes.length > 0 ?
                row.taxes.reduce((total, cost) => {
                    return total + parseFloat(cost.amount)
                }, 0) : 0;

            return <span className="amount-line-item long-text-wrap">
                       <EngageCurrency
                           amount={parseFloat(amount) === 0 ? 0 : parseFloat(amount) + totalTax}
                           style={{color: amount > 0 ? black : successGreen}}
                       />
                   </span>
        },
    },
];

const actionsColumn = [
    {
        title: <Typography.Text strong>Actions</Typography.Text>,
        dataIndex: 'actions',
        key: 'actions',
        render: (actions, row) => (
            <>
                <span className="actions-line-item long-text-wrap">Actions:</span>
                {
                    actions.map(action => {
                        switch (action) {
                            case 'edit':
                                return (
                                    <Button className="edit-button" type="link" key={action}
                                        icon={
                                            <Tooltip title="Edit">
                                                <EditTwoTone twoToneColor='#1890ff'/>
                                            </Tooltip>
                                        }
                                            onClick={() => row.editFunction(row)}>
                                    </Button>
                                )
                            case 'delete':
                                return (
                                    <Button className="delete-button" type="link" key={action}
                                        icon={
                                            <Tooltip title="Delete">
                                                <DeleteTwoTone twoToneColor='#1890ff'/>
                                            </Tooltip>
                                        }
                                            onClick={() => confirmAction(
                                              'Are you sure you want to delete this charge?',
                                              'Reconciled/Invoiced charges cannot be deleted and will be zeroed out.',
                                              row.deleteFunction,
                                              row
                                            )}>
                                    </Button>
                                )
                            default:
                                return null;
                        }
                    })
                }
            </>
        )
    }
];

const ownerColumns = [
    ...columns,
    {
        title: <Typography.Text strong>Incurred Driver</Typography.Text>,
        dataIndex: 'incurred_driver',
        key: 'incurred_driver',
        render: incurred_driver => {
            let incurredDriver = 'N/A';
            if (incurred_driver) {
                incurredDriver = incurred_driver;
            }
            return <span className="incurred-driver-line-item long-text-wrap">{incurredDriver}</span>
        },
    },
    ...actionsColumn
];

// Push so that the actions column is last column of columns or ownerColumns.
columns.push(...actionsColumn);

const ChargeHistory = ({taxes, toggleRefreshBalances}) => {
    //region State Variable setup
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const [loading, setLoading] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    const [charges, setCharges] = useState([]);
    const [refreshCharges, setRefreshCharges] = useState(false);
    const [openEditDrawer, setOpenEditDrawer] = useState(false);
    const [chargeBeingEdited, setChargeBeingEdited] = useState(null);

    // Pagination states
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(10);
    //endregion

    //region Event Handlers
    const toggleRefreshCharges = useCallback(() => {
        setRefreshCharges(!refreshCharges);
    }, [refreshCharges]);

    // When the edit icon is clicked on a row, opens the edit charge drawer passing in the charge's data.
    const openEditDrawerHandler = (charge) => {
        setChargeBeingEdited(charge);
        setOpenEditDrawer(true);
    };

    const closeEditDrawerHandler = () => {
        setChargeBeingEdited(null);
        setOpenEditDrawer(false);
    }
    //endregion

    //region API functionality
    const handleDeleteCharge = useCallback(async (charge) => {
        const abortSignal = abortSignalRef.current;
        setLoading(true);

        try {
            await deleteCharge(charge.id, abortSignal);

            if (abortSignal.aborted === false){
                showMessages(charge.status === 'pending' ? chargeDeleteSuccessMessage : chargeUpdateSuccessMessage, messageType.success)
                toggleRefreshBalances(true);
                toggleRefreshCharges();
            }
            setLoading(false);
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
        }
    }, [toggleRefreshBalances, toggleRefreshCharges]);

    // Fetch the charges associated with the account / membership.
    useEffect( () => {
        const abortSignal = abortSignalRef.current;

        const fetchCharges = async (page, perPage, abortSignal) => {
            setLoading(true);

            try {
                const account = getActiveAccount();
                if (account === null) {
                    showMessages('No Account attached!', messageType.error);
                    setLoading(false);
                    return;
                }

                const profile = getActiveProfile();
                if (profile === null) {
                    showMessages('No Profile attached!', messageType.error);
                    setLoading(false);
                    return;
                }

                const response = await getCharges(account.id, profile.id, {page, perPage}, abortSignal);

                if (abortSignal.aborted === false && response.length !== 0) {

                    // Retrieve from the B/E if the profile viewing the charges table is an owner
                    // to conditionally render the owner column. getActiveRoles does not work on
                    // a staff representing a member.
                    setIsOwner(response.is_owner);

                    // Attach two functions that deletes or edits the charge,
                    // necessary to pass in data to a table through an array of obj.
                    setCharges(response.charges.data.map(object => {
                          return {
                            ...object,
                            actions: ['edit', 'delete'],
                            deleteFunction: handleDeleteCharge,
                            editFunction: openEditDrawerHandler,
                          }
                    }))
                    setPage(response.charges.meta.current_page);
                    setPerPage(response.charges.meta.per_page);
                    setTotal(response.charges.meta.total);
                }

                setLoading(false);
            } catch (error) {
                showMessages(error.message, messageType.error);
                setLoading(false);
                if(error.status_code === 404) window.location.replace('/404');
            }
        }

        fetchCharges(page, perPage, abortSignal);
    }, [page, perPage, handleDeleteCharge]);
    //endregion

    //region Pagination logic
    const onChange = (page, perPage) => {
        setPage(page);
        setPerPage(perPage);
    }

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total,
        onChange,
        showSizeChanger: true,
        simple: window.innerWidth <= 767,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        onShowSizeChange: onChange,
    };
    //endregion

    return (
        <>
        <Spin spinning={loading}>
            <Table
                rowKey="id"
                dataSource={charges}
                columns={isOwner ? ownerColumns : columns}
                bordered={false}
                title={() =>
                   <div className="charge-history-table-title">
                       <div>
                           <Typography.Text>Charges</Typography.Text>
                           <Typography.Text className="charge-history-table-title-description">
                               {isOwner ? `These are all charges associated with the account.`
                                        : `These charges are associated with ${getAccountMembershipNumberPerson()}.`
                               }
                           </Typography.Text>
                       </div>
                       <ChargeCreateDrawer
                           taxes={taxes}
                           refreshBalances={toggleRefreshBalances}
                           refreshCharges={toggleRefreshCharges}
                       />
                       {   chargeBeingEdited &&
                           <ChargeEditDrawer
                               charge={chargeBeingEdited}
                               taxes={taxes}
                               openDrawer={openEditDrawer}
                               closeDrawer={closeEditDrawerHandler}
                               refreshBalances={toggleRefreshBalances}
                               refreshCharges={toggleRefreshCharges}
                           />
                       }
                   </div>
                }
                className="charge-history-table responsive-table"
                pagination={paginationAttr}
            />
        </Spin>
        </>
    )
}

export default ChargeHistory;

ChargeHistory.propTypes = {
    taxes: PropTypes.array.isRequired,
    toggleRefreshBalances: PropTypes.func.isRequired,
}
